/**
 * TenantContext — owner multi-tenant switcher (Prompt 11E.5, build item 5 / D9).
 *
 * Only relevant to `owner` accounts provisioned for multiple tenants
 * (`user.available_tenant_ids.length > 1`). Every other role/account has a
 * single-entry `available_tenant_ids` and this context is effectively a
 * no-op passthrough to the user's own `tenant_id`.
 *
 * Selection persists per-uid in localStorage and is pushed into the API
 * client (`setActiveTenantId`) so every `/v1/*` call carries the selected
 * tenant as `X-Tenant-Id` without threading tenant state through every
 * hook call site. The backend independently validates the header against
 * the caller's claims (403 if outside `available_tenant_ids`) — this
 * context is a UX convenience, not a security boundary.
 *
 * "All" mode (`ALL_TENANTS` sentinel):
 *   - Read-only. No default `X-Tenant-Id` is set (so any code path that
 *     doesn't explicitly fan out — e.g. dashboards, forms — targets the
 *     caller's own home tenant, never "all tenants" by accident).
 *   - The Entity Framework (`useTenantAwareEntityList` in
 *     `src/components/entity/`) is the only place that fans out — it issues
 *     one request per tenant with an explicit per-call `X-Tenant-Id`
 *     override and merges the results with a synthetic Tenant column.
 *   - Mutating UI (add/edit/archive/activate) must check `isAllMode` and
 *     disable itself with a "select a tenant" prompt — enforced today in
 *     `EntityTable` for entity-framework consumers.
 */

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/context';
import { setActiveTenantId } from '@core/api/client';

export const ALL_TENANTS = '__all__' as const;
export type TenantSelection = string | typeof ALL_TENANTS;

interface TenantContextType {
  /** Every tenant this account may act as (from claims via /v1/auth/me). */
  availableTenantIds: string[];
  /** Currently selected tenant, or the ALL_TENANTS sentinel. */
  selectedTenantId: TenantSelection;
  /** True when the "All" option is selected — read-only, no mutations. */
  isAllMode: boolean;
  /** Show the switcher only for accounts with >1 available tenant. */
  isSwitcherVisible: boolean;
  setSelectedTenantId: (id: TenantSelection) => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

function storageKey(uid: string): string {
  return `candeza:selectedTenant:${uid}`;
}

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // `null` = not resolved yet. Deliberately NOT ALL_TENANTS: defaulting to
  // the All sentinel made every user — including single-tenant non-owners —
  // spend their first render in read-only "All" mode with pagination hidden,
  // and fan out list queries over an empty tenant array, which renders as
  // "no results" rather than a loading state.
  const [selectedTenantId, setSelectedTenantIdState] = useState<TenantSelection | null>(null);
  // Last selection pushed into the API client, so we only clear cached query
  // data on a genuine switch (not on first resolution).
  const appliedRef = useRef<TenantSelection | null>(null);

  const availableTenantIds = useMemo(
    () => user?.available_tenant_ids ?? (user ? [user.tenant_id] : []),
    [user],
  );

  const isMultiTenant = availableTenantIds.length > 1;

  // Initialise / re-initialise selection whenever the signed-in user changes.
  useEffect(() => {
    if (!user) {
      setSelectedTenantIdState(null);
      appliedRef.current = null;
      setActiveTenantId(null);
      return;
    }
    const stored = window.localStorage.getItem(storageKey(user.uid));
    // A stored ALL_TENANTS is only honoured while the account actually has
    // more than one tenant. Otherwise an owner downgraded to a single tenant
    // stays stuck in read-only All mode with the switcher hidden (it needs
    // >1 tenant to render) and no way back out.
    const storedIsUsable =
      stored !== null &&
      ((stored === ALL_TENANTS && isMultiTenant) || availableTenantIds.includes(stored));
    setSelectedTenantIdState(storedIsUsable ? (stored as TenantSelection) : user.tenant_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, isMultiTenant]);

  // Push the resolved selection into the API client + persist it.
  useEffect(() => {
    if (!user || selectedTenantId === null) return;

    const previous = appliedRef.current;
    appliedRef.current = selectedTenantId;
    setActiveTenantId(selectedTenantId === ALL_TENANTS ? null : selectedTenantId);
    window.localStorage.setItem(storageKey(user.uid), selectedTenantId);

    // Switching tenants invalidates EVERY cached response: react-query keys
    // across the app are not tenant-qualified, so without this the previous
    // tenant's rows keep rendering (and `placeholderData: keepPrevious`
    // shows them even while the new fetch is in flight).
    if (previous !== null && previous !== selectedTenantId) {
      void queryClient.clear();
    }
  }, [selectedTenantId, user, queryClient]);

  const setSelectedTenantId = (id: TenantSelection): void => {
    setSelectedTenantIdState(id);
  };

  const value: TenantContextType = {
    availableTenantIds,
    // Before resolution, report the user's own tenant rather than the All
    // sentinel so nothing reads as "All" during the first paint.
    selectedTenantId: selectedTenantId ?? user?.tenant_id ?? '',
    isAllMode: selectedTenantId === ALL_TENANTS,
    // Non-owners and single-tenant owners never see the switcher — the
    // spec's explicit "non-owner never sees the switcher" acceptance check.
    isSwitcherVisible: user?.role === 'owner' && isMultiTenant,
    setSelectedTenantId,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTenant = (): TenantContextType => {
  const ctx = useContext(TenantContext);
  if (ctx === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return ctx;
};

